import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import Masonry from 'react-masonry-css';
import './CRTDetail.css';
import { Modal, Button, Container } from 'react-bootstrap';
import { AuthContext } from '../contexts/AuthContext';
import { Helmet } from 'react-helmet';
import CRTSubmissionModal from '../components/CRTSubmissionModal';
import { PersonFill } from 'react-bootstrap-icons';

const electronUrl = process.env.REACT_APP_ELECTRON_URL;

const CRTDetail = () => {
  const { slug } = useParams();
  const [crt, setCrt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authData, setAuthData] = useState(null);
  const [authDataLoading, setAuthDataLoading] = useState(false);
  const { isAuthenticated, validateToken, role, user, token } = useContext(AuthContext);

  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);

  const openModal = (imgUrl, imageIndex = 0, isFromCommunity = false) => {
    // Create array of all images (main gallery + community images)
    const gallery = crt.gallery_images || [];
    const communityImages = [];
    
    // Collect all community product images
    if (crt.related_products && crt.related_products.length > 0) {
      crt.related_products.forEach(product => {
        if (product.images && product.images.length > 0) {
          communityImages.push(...product.images);
        }
      });
    }
    
    // Combine all images
    const allImagesList = [...gallery, ...communityImages];
    
    let targetIndex = imageIndex;
    
    // If this is a community image, find its index in the combined array
    if (isFromCommunity && imgUrl) {
      const foundIndex = allImagesList.findIndex(img => 
        (img.url === imgUrl || img.compressed_url === imgUrl)
      );
      if (foundIndex !== -1) {
        targetIndex = foundIndex;
      }
    }
    
    setAllImages(allImagesList);
    setCurrentImageIndex(targetIndex);
    setModalImage(allImagesList[targetIndex]);
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setModalImage(null);
    setCurrentImageIndex(0);
    setAllImages([]);
  };

  const goToPrevious = () => {
    if (allImages.length > 1) {
      const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : allImages.length - 1;
      setCurrentImageIndex(newIndex);
      setModalImage(allImages[newIndex]);
    }
  };

  const goToNext = () => {
    if (allImages.length > 1) {
      const newIndex = currentImageIndex < allImages.length - 1 ? currentImageIndex + 1 : 0;
      setCurrentImageIndex(newIndex);
      setModalImage(allImages[newIndex]);
    }
  };

  const openOriginalImage = () => {
    if (modalImage && modalImage.url) {
      window.open(modalImage.url, '_blank');
    }
  };

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!showModal) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
        case 'Escape':
          event.preventDefault();
          closeModal();
          break;
        default:
          break;
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [showModal, currentImageIndex, allImages.length]);

  const breakpointColumnsObj = {
    default: 3,
    768: 2,
    480: 1
  };

  useEffect(() => {
    const fetchCRT = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/crts/public/${slug}`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setCrt(data);
      } catch (err) {
        console.error(err);
        setCrt(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCRT();
  }, [slug]);
  
  // Fetch authenticated data if user is authenticated
  useEffect(() => {
    const fetchAuthenticatedData = async () => {
      if (!isAuthenticated || !token || !crt) return;
      
      setAuthDataLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/crts/circuit/${slug}`, 
          { 
            headers: { 
              Authorization: `Bearer ${token}` 
            } 
          }
        );
        
        if (res.ok) {
          const data = await res.json();
          // Store the response, which includes manuals
          setAuthData(data);
        }
      } catch (err) {
        console.error('Error fetching authenticated data:', err);
      } finally {
        setAuthDataLoading(false);
      }
    };
    
    fetchAuthenticatedData();
  }, [slug, isAuthenticated, token, crt]);

  if (loading) return (
    <Container>
      <h2 className="crt-title"></h2>
      <div>Loading...</div>
    </Container>
  );
  if (!crt) return (
    <Container>
      <h2 className="crt-title">Not found</h2>
      <div>CRT not found.</div>
    </Container>
  );

  const format = (val) => Array.isArray(val) ? val.join(', ') : val;

  // Helper function to format resistance values
  const formatResistance = (value) => {
    if (!value) return '';
    
    // Extract numeric value if it's a string with numbers
    const numericValue = typeof value === 'string' 
      ? parseFloat(value.replace(/[^\d.]/g, '')) 
      : parseFloat(value);
    
    // If we couldn't extract a valid number, return the original value
    if (isNaN(numericValue)) return value;
    
    // Format as kΩ if over 1000 ohms
    if (numericValue >= 1000) {
      const kOhms = numericValue / 1000;
      return `${kOhms % 1 === 0 ? kOhms.toFixed(0) : kOhms.toFixed(1)}kΩ`;
    }
    
    // Otherwise return as Ω
    return `${numericValue}Ω`;
  };

  // Helper function to format boolean values as Yes/No
  const formatBooleanValue = (value) => {
    if (value === true || value === 'true' || value === 'TRUE' || value === 'yes' || value === 'Yes' || value === 'YES') {
      return 'Yes';
    }
    if (value === false || value === 'false' || value === 'FALSE' || value === 'no' || value === 'No' || value === 'NO') {
      return 'No';
    }
    return value;
  };

  const gallery = crt.gallery_images || [];
  const heroImage = gallery.length > 0 ? gallery[0] : null;
  const remainingImages = gallery.slice(1);
  
  // Get manuals from authenticated data if available (legacy)
  const manuals = authData?.manuals || [];
  
  // Get shared documents from authenticated data if available (new system)
  const sharedDocuments = authData?.shared_documents_details || [];

  const isModdable = (label, value, link = null) => {
    if (value === 'Yes') {
      return link ? (
        <a href={link} className="mod-label mod-label-link bg-success" target="_blank" rel="noreferrer">
          {label}
        </a>
      ) : (
        <span className="mod-label bg-success">{label}</span>
      );
    } else if (value === 'No' && label === 'RGB Moddable') {
      return <span className="mod-label mod-label-not bg-danger">Not {label}</span>;
    }
    return null;
  };

  const renderInfo = (label, value) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <div className="info-item">
        <strong>{label}:</strong> {format(value)}
      </div>
    );
  };

  const renderModBoards = () => {
    if (!crt.mod_boards || crt.mod_boards.length === 0) return null;
    
    return (
      <div className="mod-boards-section mt-2">
        {crt.mod_boards.map((board, idx) => {
          return (
            board.purchase_links && board.purchase_links.length > 0 &&
              board.purchase_links.map((link, linkIdx) => (
                <a
                  key={linkIdx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mod-board-purchase-link"
                >
                  {board.name}
                </a>
              ))
            )
        })} 
      </div>
    );
  };

  // Ensure rgb_mux_calculator_enabled is boolean
  const rgbMuxCalculatorEnabled = crt.rgb_mux_calculator_enabled === true || crt.rgb_mux_calculator_enabled === 'true';

  // Helper function to parse and display manual links
  const parseManualLinks = (manualLinks) => {
    // Handle null, undefined, empty strings, and whitespace-only strings
    if (!manualLinks || (typeof manualLinks === 'string' && !manualLinks.trim())) {
      return [];
    }
    
    try {
      // If it's a JSON string, parse it
      if (typeof manualLinks === 'string' && manualLinks.trim().startsWith('[')) {
        const parsed = JSON.parse(manualLinks);
        if (Array.isArray(parsed)) {
          // Filter out invalid entries (empty URLs, etc.)
          return parsed.filter(link => 
            link && 
            typeof link === 'object' && 
            link.url && 
            typeof link.url === 'string' && 
            link.url.trim()
          );
        }
        return [];
      }
      
      // If it's already an array, filter out invalid entries
      if (Array.isArray(manualLinks)) {
        return manualLinks.filter(link => 
          link && 
          typeof link === 'object' && 
          link.url && 
          typeof link.url === 'string' && 
          link.url.trim()
        );
      }
      
      // If it's a comma-separated string, convert to simple link objects
      if (typeof manualLinks === 'string') {
        const urls = manualLinks.split(',')
          .map(url => url.trim())
          .filter(url => url && url.length > 0);
        
        if (urls.length === 0) {
          return [];
        }
        
        return urls.map(url => ({ 
          url, 
          custom_name: null, 
          label: null, 
          type: 'unknown' 
        }));
      }
    } catch (e) {
      // If parsing fails, treat as comma-separated string
      if (typeof manualLinks === 'string') {
        const urls = manualLinks.split(',')
          .map(url => url.trim())
          .filter(url => url && url.length > 0);
        
        if (urls.length === 0) {
          return [];
        }
        
        return urls.map(url => ({ 
          url, 
          custom_name: null, 
          label: null, 
          type: 'unknown' 
        }));
      }
    }
    
    return [];
  };

  // Helper function to get display name for manual link
  const getManualDisplayName = (link, index) => {
    // Priority: custom_name > label > type-based name > generic fallback
    if (link.custom_name) {
      return link.custom_name;
    }
    if (link.label) {
      return link.label;
    }
    if (link.type && link.type !== 'unknown') {
      return `${link.type.charAt(0).toUpperCase() + link.type.slice(1)} Manual`;
    }
    return `Manual ${index + 1}`;
  };

  return (
    <>
      {crt && crt.title && (
        <Helmet>
          <title>{crt.title} | Sunthar's Super Sector</title>
        </Helmet>
      )}
      <Container>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <h2 className="crt-title mb-0 me-2">{crt.title}</h2>
          {isAuthenticated && role === 'super' && (
            <a 
              href={`/superadmin/edit-crt/${crt._id}`}
              className="btn btn-outline-primary btn-sm"
            >
              Edit CRT
            </a>
          )}
          {crt.rgb_modifiable === 'Yes' && (
            <a href={`/rgb-mux-calculator?crt_slug=${slug}`} className="btn btn-outline-light btn-sm">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calculator me-1" viewBox="0 0 16 16">
                <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h8zM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4z"/>
                <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-2zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-4z"/>
              </svg>
              RGB Calculator
            </a>
          )}
          <Button 
            variant="outline-success" 
            size="sm"
            onClick={() => setShowSubmissionModal(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-camera me-1" viewBox="0 0 16 16">
              <path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1v6zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4H2z"/>
              <path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm0 1a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM3 6.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0z"/>
            </svg>
            Submit Images
          </Button>
        </div>
        
        <div className="crt-detail-grid">
          {/* Left (2/3) */}
          <div className="crt-main">
            <div className="meta mb-3">
              <small className="text-muted">
                Created: {new Date(crt.created_at).toLocaleString()} | Updated: {new Date(crt.updated_at).toLocaleString()}
              </small>
            </div>
            {crt.image && (
              <div className="crt-image">
                <img src={crt.image} alt={crt.title} />
              </div>
            )}

            <div className="mod-labels">
              {isModdable('RGB Moddable', crt.rgb_modifiable, crt.rgb_mod_link)}
              {isModdable('Component Moddable', crt.component_modifiable, crt.component_mod_link)}
              {isModdable('S-Video Moddable', crt.svideo_modifiable)}
              {isModdable('Composite Moddable', crt.composite_modifiable)}
            </div>

            {crt.summary && (
              <div className="crt-summary mt-4 mb-4">
                <h5>Overview</h5>
                <div className="markdown-body">
                  <ReactMarkdown 
                    remarkPlugins={[remarkBreaks]}
                    components={{
                      // This ensures a line break is created for each newline character
                      p: ({node, ...props}) => {
                        // Replace single newlines with <br /> tags
                        const content = React.Children.map(props.children, child => {
                          if (typeof child === 'string') {
                            return child.split('\n').reduce((result, text, i) => {
                              if (i === 0) return [text];
                              return [...result, <br key={i} />, text];
                            }, []);
                          }
                          return child;
                        });
                        return <p {...props}>{content}</p>;
                      }
                    }}
                  >
                    {crt.summary}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {crt.rgb_mod_link && (
              <a
                href={crt.rgb_mod_link}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-light btn-sm me-2"
              >
                View RGB Mod Guide
              </a>
            )}

            {crt.crt_details_link && (
              <a
                href={crt.crt_details_link}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-light btn-sm me-2"
              >
                View General CRT Details
              </a>
            )}

            {remainingImages.length > 0 && (
              <div className="crt-gallery mt-4">
                <h5>Gallery</h5>
                <Masonry
                  breakpointCols={breakpointColumnsObj}
                  className="gallery-grid masonry-grid"
                  columnClassName="masonry-grid_column"
                >
                  {remainingImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={img.compressed_url || img.url}
                      alt={`Gallery image ${idx + 2}`}
                      className="gallery-thumb"
                      onClick={() => openModal(null, idx + 1)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Masonry>
              </div>
            )}

            {/* RGB MUX Calculator SVG */}
            {rgbMuxCalculatorEnabled && (
              <div className="mt-4">
                <h5>RGB MUX Diagram</h5>
                <a href={`/rgb-mux-calculator?crt_slug=${slug}`}>
                  <img 
                    src={`${process.env.REACT_APP_API_URL}/svg/rgb-mux/${slug}?format=png`}
                    alt="RGB MUX Calculator Diagram"
                    className="img-fluid"
                    style={{ maxWidth: '100%', borderRadius: '8px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="alert alert-warning" style={{ display: 'none' }}>
                    Failed to load RGB MUX diagram. Click to open the calculator.
                  </div>
                </a>
              </div>
            )}

            {/* Related Products Section */}
            {crt.related_products && crt.related_products.length > 0 && (
              <div className="crt-community-content mt-4 mb-4">
                <h5>Community Contributions</h5>
                
                <div className="community-content-description mb-3">
                  Products, articles, and services related to the {crt.title} from our community.
                </div>
                
                {/* Display each product/article as a separate section */}
                {crt.related_products.map((product, productIdx) => (
                  <div key={productIdx} className="community-product-section mb-4">
                    {/* Product header with name, store, and date */}
                    <div className="community-product-header">
                      <h6 className="community-product-title">
                        {product.store_handle && product.slug ? (
                        <a 
                          href={`${electronUrl}/${product.store_handle}/product/${product.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {product.name}
                        </a>
                        ) : (
                          product.name
                        )}
                      </h6>
                      <div className="community-product-meta">
                        {product.author ? (
                          <span className="community-store-name">
                            by {product.author}
                        </span>
                        ) : (
                          <span className="community-store-name">
                            by {product.store_name}
                          </span>
                        )}
                        <span className="community-product-type">
                          {product.type === 'sale' && 'Product'}
                          {product.type === 'service' && 'Service'}
                          {product.type === 'showcase' && 'Article'}
                          {product.type === 'affiliate' && 'Affiliate'}
                        </span>
                        <span className="community-product-date">
                          {new Date(product.created_at || Date.now()).toLocaleDateString(undefined, { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                        {product.type === 'sale' && product.price && (
                          <span className="community-product-price">
                            ${(product.price / 100).toFixed(2)}
                          </span>
                        )}
                      </div>
                      
                      {/* Product intro/description */}
                      {product.intro && (
                        <div className="community-product-intro">
                          {product.intro}
                        </div>
                      )}
                    </div>
                    
                    {/* Images for this product */}
                    {product.images && product.images.length > 0 ? (
                      <Masonry
                        breakpointCols={breakpointColumnsObj}
                        className="gallery-grid masonry-grid"
                        columnClassName="masonry-grid_column"
                      >
                        {product.images.map((image, imageIdx) => (
                          <img
                            key={imageIdx}
                            src={image.compressed_url || image.url}
                            alt={`${product.name} - image ${imageIdx + 1}`}
                            className="gallery-thumb community-thumb"
                            onClick={() => openModal(image.url || image.compressed_url, 0, true)}
                            style={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Masonry>
                    ) : (
                      <div className="gallery-thumb community-placeholder">
                        <div className="placeholder-content">
                          <span className="placeholder-type">
                            {product.type === 'showcase' ? 'Article' : 'Product'}
                          </span>
                          {product.store_handle && product.slug && (
                          <a 
                            href={`${electronUrl}/${product.store_handle}/product/${product.slug}`}
                            className="placeholder-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

          </div>

          {/* Right (1/3) */}
          <div className="crt-sidebar">
            {heroImage && (
            <div className="crt-hero-image mb-3">
              <img
                src={heroImage.compressed_url || heroImage.url}
                alt={crt.title}
                className="img-fluid"
                style={{ borderRadius: '8px', maxWidth: '100%', cursor: 'pointer' }}
                onClick={() => openModal(null, 0)}
              />
            </div>
            )}
            {/* Only show Specs if at least one spec field is present */}
            {(
              crt.chassis || crt.chroma || crt.viewable_size || crt.osd || crt.tube || crt.power || crt.launched || crt.country_of_manufacture || crt.inputs || crt.speakers || crt.url
            ) && (
              <>
                <h6>Specs</h6>
                <div className="info-card mb-3">
                  {renderInfo('Chassis', crt.chassis)}
                  {renderInfo('Chroma IC (Jungle)', crt.chroma)}
                  {renderInfo('OSD IC', crt.osd)}
                  {/* Handle both old and new tube format */}
                  {(() => {
                    // Check if we have valid tubes data
                    const validTubes = crt.tubes && Array.isArray(crt.tubes) 
                      ? crt.tubes.filter(tube => 
                          tube && 
                          tube.model && 
                          tube.model.trim() !== '' && 
                          tube.model.toLowerCase() !== 'test' &&
                          (!tube.brand || tube.brand.toLowerCase() !== 'tube')
                        )
                      : [];
                    
                    if (validTubes.length > 0) {
                      return renderInfo('Tube', 
                        validTubes.map(tube => 
                          `${tube.model}${tube.brand && tube.brand.trim() ? ` (${tube.brand})` : ''}`
                        ).join(', ')
                      );
                    } else {
                      // Fallback to old format, but filter out obviously invalid entries
                      const tubeData = Array.isArray(crt.tube) 
                        ? crt.tube.filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'test') 
                        : crt.tube && crt.tube.trim() !== '' && crt.tube.toLowerCase() !== 'test' 
                          ? [crt.tube] 
                          : [];
                      
                      return tubeData.length > 0 ? renderInfo('Tube', tubeData) : null;
                    }
                  })}
                  {renderInfo('Viewable Size', crt.viewable_size)}
                  {renderInfo('Power (W)', crt.power)}
                  {renderInfo('Launched', crt.launched)}
                  {renderInfo('Country of Manufacture', crt.country_of_manufacture)}
                  {renderInfo('Inputs', crt.inputs)}
                  {renderInfo('Stereo', crt.chassis_stereo ? 'Yes' : 'No')}
                </div>
              </>
            )}
            
            {/* Credits and Attribution Section */}
            {(crt.authors && crt.authors.length > 0) || (crt.related_products && crt.related_products.length > 0) ? (
              <>
                <h6>Credits and Attribution</h6>
                <div className="info-card mb-3">
                  {/* Core Authors */}
                  {crt.authors && crt.authors.map((author, idx) => (
                    <div key={`author-${idx}`} className="credit-item mb-2 d-flex align-items-center">
                      <div>
                        {author.url ? (
                          <>
                            <PersonFill className="me-1" />
                            <a href={author.url} target="_blank" rel="noopener noreferrer" className='me-1'>
                              <strong>{author.author_name}</strong> 
                            </a>
                            ({author.role})
                          </>
                        ) : (
                          <span>
                            <PersonFill className="me-1" />
                            <strong>{author.author_name}</strong> ({author.role})
                          </span>
                        )}
                        {author.notes && (
                          <div className="text-muted">{author.notes}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Community Contributors */}
                  {crt.related_products && crt.related_products
                    .filter(product => product.author || product.store_name)
                    .map((product, idx) => (
                      <div key={`contributor-${idx}`} className="credit-item mb-2 d-flex align-items-center">
                        <div>
                          {product.store_handle && product.slug ? (
                            <>
                              <PersonFill className="me-1" />
                              <a 
                                href={`${electronUrl}/${product.store_handle}/product/${product.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className='me-1'
                              >
                                <strong>{product.author || product.store_name}</strong>
                              </a>
                              (contributor)
                            </>
                          ) : (
                            <span>
                              <PersonFill className="me-1" />
                              <strong>{product.author || product.store_name}</strong> (contributor)
                            </span>
                          )}
                          <div className="text-muted">Showcase article</div>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            ) : null}
            
            {crt.mod_boards && crt.mod_boards.length > 0 && (
              <>
                <h6>Compatible mod kits</h6>
                <div className="mb-3">
                  {renderModBoards()}
                </div>
              </>
            )}
            
            {/* MUX Board Information */}
            {crt.rgb_modifiable === 'Yes' && isAuthenticated && authData && (
              <div className="mt-3">
                <h6>MUX Board Information</h6>
                <div className="info-card mb-3">
                  {/* MUX Components Section */}
                  {rgbMuxCalculatorEnabled ? (
                    (authData.mux_termination_resistors || 
                      authData.mux_rgb_input_resistors || 
                      authData.mux_audio_resistors || 
                      authData.mux_blanking_diode ||
                      authData.mux_transistor) ? (
                      <div className="mb-3">
                        <table className="table table-sm" style={{ fontSize: '0.7rem' }}>
                          <tbody>
                            {authData.mux_termination_resistors && (
                              <tr>
                                <td className="fw-bold text-nowrap">Termination (R1, R2, R3)</td>
                                <td className="text-end">{formatResistance(authData.mux_termination_resistors)}</td>
                              </tr>
                            )}
                            {/* Show either capacitors or resistors for R4, R5, R6 based on direct injection mode */}
                            {(authData.rgb_direct_injection === 'true' || authData.rgb_direct_injection === true) ? (
                              authData.mux_rgb_input_capacitors && (
                                <tr>
                                  <td className="fw-bold text-nowrap">RGB Capacitors (R4, R5, R6)</td>
                                  <td className="text-end">{authData.mux_rgb_input_capacitors} μF</td>
                                </tr>
                              )
                            ) : (
                              authData.mux_rgb_input_resistors && (
                                <tr>
                                  <td className="fw-bold text-nowrap">RGB Input (R4, R5, R6)</td>
                                  <td className="text-end">{formatResistance(authData.mux_rgb_input_resistors)}</td>
                                </tr>
                              )
                            )}
                            {authData.mux_audio_resistors && (
                              <tr>
                                <td className="fw-bold text-nowrap">Audio Resistors (R7, R8)</td>
                                <td className="text-end">{formatResistance(authData.mux_audio_resistors)}</td>
                              </tr>
                            )}
                            {authData.mux_blanking_diode && (
                              <tr>
                                <td className="fw-bold text-nowrap">Blanking Diode (R9 or D9)</td>
                                <td className="text-end">{formatBooleanValue(authData.mux_blanking_diode)}</td>
                              </tr>
                            )}
                            {authData.mux_blanking_ground_resistor && (
                              <tr>
                                <td className="fw-bold text-nowrap">Blanking Ground Resistor (R10)</td>
                                <td className="text-end">{formatResistance(authData.mux_blanking_ground_resistor)}</td>
                              </tr>
                            )}
                            {authData.mux_blanking_inline_resistor && (
                              <tr>
                                <td className="fw-bold text-nowrap">Blanking Inline Resistor (R11)</td>
                                <td className="text-end">{formatResistance(authData.mux_blanking_inline_resistor)}</td>
                              </tr>
                            )}
                            {authData.mux_transistor && (
                              <tr>
                                <td className="fw-bold text-nowrap">Transistor (Q1)</td>
                                <td className="text-end">{formatBooleanValue(authData.mux_transistor)}</td>
                              </tr>
                            )}
                            {authData.mux_transistor_base_resistor && (
                              <tr>
                                <td className="fw-bold text-nowrap">Transistor Base Resistor (R12)</td>
                                <td className="text-end">{formatResistance(authData.mux_transistor_base_resistor)}</td>
                              </tr>
                            )}
                            {authData.analog_chroma_vpp && (
                              <tr>
                                <td className="fw-bold text-nowrap">Analog Chroma Vpp</td>
                                <td className="text-end">{authData.analog_chroma_vpp}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {/* Calculator Link */}
                        {rgbMuxCalculatorEnabled && (
                          <div className="mt-3">
                            <a 
                              href={`/rgb-mux-calculator?crt_slug=${slug}`} 
                              className="btn btn-primary btn-sm"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calculator me-1" viewBox="0 0 16 16">
                                <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h8zM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4z"/>
                                <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-2zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-4z"/>
                              </svg>
                              Open RGB MUX Calculator
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-muted">No MUX board information available for this CRT.</div>
                    )
                  ) : (
                    <div className="text-muted">No MUX board information available for this CRT.</div>
                  )}
                </div>
              </div>
            )}
            
            {/* Login prompt shown if either RGB mod notes, manuals, or shared documents are available but user is not logged in */}
            {!isAuthenticated && (crt.has_rgb_mod_notes || crt.has_manuals || crt.has_shared_documents) && (
              <div className="mt-3">
                <h6>Additional Resources</h6>
                <div className="login-prompt alert alert-info mb-3">
                  <p className="mb-1">Sign in to access:</p>
                  <ul className="mb-2">
                    {crt.has_rgb_mod_notes && <li>Detailed RGB Modification Notes</li>}
                    {(crt.has_manuals || crt.has_shared_documents) && <li>Service Manuals &amp; Documentation</li>}
                  </ul>
                  <a href="/login" className="btn btn-primary btn-sm">Sign In</a>
                </div>
              </div>
            )}
            
            {/* Documentation Section - Only shown if there are valid manual links */}
            {(() => {
              const manualLinksArray = parseManualLinks(crt.manual_links);
              
              if (manualLinksArray.length === 0) {
                return null;
              }
              
              return (
                <div className="mt-3">
                  <h6>Online Manuals</h6>
                  <div className="info-card mb-3">
                    {manualLinksArray.map((link, idx) => (
                      <div key={idx} className="manual-link-item mb-2 d-flex align-items-center">
                        <div className="manual-icon me-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-filetype-pdf" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5L14 4.5ZM1.6 11.85H0v3.999h.791v-1.342h.803c.287 0 .531-.057.732-.173.203-.117.358-.275.463-.474a1.42 1.42 0 0 0 .161-.677c0-.25-.053-.476-.158-.677a1.176 1.176 0 0 0-.46-.477c-.2-.12-.443-.179-.732-.179Zm.545 1.333a.795.795 0 0 1-.085.38.574.574 0 0 1-.238.241.794.794 0 0 1-.375.082H.788V12.48h.66c.218 0 .389.06.512.181.123.122.185.296.185.522Zm1.217-1.333v3.999h1.46c.401 0 .734-.08.998-.237a1.45 1.45 0 0 0 .595-.689c.13-.3.196-.662.196-1.084 0-.42-.065-.778-.196-1.075a1.426 1.426 0 0 0-.589-.68c-.264-.156-.599-.234-1.005-.234H3.362Zm.791.645h.563c.248 0 .45.05.609.152a.89.89 0 0 1 .354.454c.079.201.118.452.118.753a2.3 2.3 0 0 1-.068.592 1.14 1.14 0 0 1-.196.422.8.8 0 0 1-.334.252 1.298 1.298 0 0 1-.483.082h-.563v-2.707Zm3.743 1.763v1.591h-.79V11.85h2.548v.653H7.896v1.117h1.606v.638H7.896Z"/>
                          </svg>
                        </div>
                        <div className="manual-info flex-grow-1">
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noreferrer"
                            title={link.url}
                          >
                            {getManualDisplayName(link, idx)}
                          </a>
                          {link.type && link.type !== 'unknown' && (
                            <span className="manual-type ms-2 badge bg-primary">
                              {link.type.charAt(0).toUpperCase() + link.type.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Display uploaded manuals (only for authenticated users) */}
            {isAuthenticated && (
              <>
                {/* RGB Mod Notes - Only for authenticated users */}
                {authData?.rgb_mod_notes && (
                  <div className="mt-3">
                    <h6>RGB Modification Notes</h6>
                    <div className="mod-notes markdown-body info-card mb-3">
                      <ReactMarkdown remarkPlugins={[remarkBreaks]}>{authData.rgb_mod_notes}</ReactMarkdown>
                    </div>
                  </div>
                )}
                
                {/* Shared Documents (New System) */}
                {sharedDocuments.length > 0 && (
                  <div className="mt-3">
                    <h6>Downloadable Manuals</h6>
                    <div className="shared-documents-list info-card mb-3">
                      {sharedDocuments.map((doc, idx) => (
                        <div key={idx} className="manual-item d-flex align-items-center mb-2">
                          <div className="manual-icon me-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-filetype-pdf" viewBox="0 0 16 16">
                              <path fillRule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5L14 4.5ZM1.6 11.85H0v3.999h.791v-1.342h.803c.287 0 .531-.057.732-.173.203-.117.358-.275.463-.474a1.42 1.42 0 0 0 .161-.677c0-.25-.053-.476-.158-.677a1.176 1.176 0 0 0-.46-.477c-.2-.12-.443-.179-.732-.179Zm.545 1.333a.795.795 0 0 1-.085.38.574.574 0 0 1-.238.241.794.794 0 0 1-.375.082H.788V12.48h.66c.218 0 .389.06.512.181.123.122.185.296.185.522Zm1.217-1.333v3.999h1.46c.401 0 .734-.08.998-.237a1.45 1.45 0 0 0 .595-.689c.13-.3.196-.662.196-1.084 0-.42-.065-.778-.196-1.075a1.426 1.426 0 0 0-.589-.68c-.264-.156-.599-.234-1.005-.234H3.362Zm.791.645h.563c.248 0 .45.05.609.152a.89.89 0 0 1 .354.454c.079.201.118.452.118.753a2.3 2.3 0 0 1-.068.592 1.14 1.14 0 0 1-.196.422.8.8 0 0 1-.334.252 1.298 1.298 0 0 1-.483.082h-.563v-2.707Zm3.743 1.763v1.591h-.79V11.85h2.548v.653H7.896v1.117h1.606v.638H7.896Z"/>
                            </svg>
                          </div>
                          <div className="manual-info flex-grow-1 text-truncate">
                            <a 
                              href={doc.url} 
                              target="_blank" 
                              rel="noreferrer"
                              title={doc.custom_name || doc.display_name}
                            >
                              {doc.custom_name || doc.display_name}
                            </a>
                            {doc.type && (
                              <span className="manual-type ms-2 badge bg-primary">
                                {doc.type}
                              </span>
                            )}
                            {doc.file_size && (
                              <small className="text-muted ms-2">
                                ({(doc.file_size / (1024 * 1024)).toFixed(2)} MB)
                              </small>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Loading indicator for authenticated content */}
            {isAuthenticated && authDataLoading && (
              <div className="mt-3 text-center p-3 info-card mb-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 mb-0 text-muted">Loading documentation and RGB mod information...</p>
              </div>
            )}
          </div>
        </div>
        <Modal show={showModal} onHide={closeModal} centered size="lg">
          <Modal.Body className="p-0 position-relative">
            {modalImage && (
              <div className="modal-image-container">
                <img
                  src={modalImage.compressed_url || modalImage.url}
                  alt="Zoomed"
                  className="modal-image"
                  onClick={openOriginalImage}
                  title="Click to view original image"
                />
              </div>
            )}
            
            {/* Navigation arrows - only show if there are multiple images */}
            {allImages.length > 1 && (
              <>
                <button
                  className="position-absolute top-50 start-0 translate-middle-y ms-2 modal-nav-btn"
                  onClick={goToPrevious}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
                  </svg>
                </button>
                <button
                  className="position-absolute top-50 end-0 translate-middle-y me-2 modal-nav-btn"
                  onClick={goToNext}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </button>
              </>
            )}
            
            {/* Close button */}
            <button
              className="position-absolute top-0 end-0 mt-2 me-2 modal-nav-btn"
              onClick={closeModal}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-x-lg" viewBox="0 0 16 16">
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
              </svg>
            </button>
            
            {/* Image counter */}
            {allImages.length > 1 && (
              <div className="position-absolute top-0 start-50 translate-middle-x mt-2">
                <span className="badge bg-dark">
                  {currentImageIndex + 1} of {allImages.length}
                </span>
              </div>
            )}
          </Modal.Body>
        </Modal>
        <CRTSubmissionModal
          show={showSubmissionModal}
          onHide={() => setShowSubmissionModal(false)}
          existingCrtTitle={crt?.title || ''}
          existingCrtId={crt?._id}
          existingCrtSlug={slug}
          imageOnly={true}
          submissionType="contribution"
        />
      </Container>
    </>
  );
};

export default CRTDetail;
